import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { UserProfileDropdown, type NotificationItem } from "@/components/common/UserProfileDropdown";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { USER_ROUTES } from "@/routes";
import { PUBLIC_ROUTES } from "@/routes";
import { getEvents } from "@/services/api/events";
import { getGiftRegistry } from "@/services/api/eventSettings";
import { getEventGifts } from "@/services/api/gifts";
import { fetchStripeAccountStatus } from "@/services/api/stripeConnect";
import { getUserCredits, type UserCreditsResponse } from "@/services/api/eventPlans";
import { CreditsDisplay } from "@/components/customer/CreditsDisplay";
import { ActiveEventReturn } from "@/components/customer/events/ActiveEventReturn";

type UserNavKey = "events" | "pachete";

interface UserHeaderProps {
  currentLocale: string;
  active: UserNavKey;
}

export function UserHeader({
  currentLocale,
  active,
}: UserHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const [giftNotifications, setGiftNotifications] = useState<NotificationItem[]>([]);
  const [credits, setCredits] = useState<UserCreditsResponse | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("jubiel.notifications.dismissed");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setDismissedNotificationIds(parsed.filter((v) => typeof v === "string"));
      }
    } catch {
      // ignore
    }
  }, []);

  const dismissNotification = (id: string) => {
    setDismissedNotificationIds((prev) => {
      const next = Array.from(new Set([...prev, id]));
      try {
        localStorage.setItem("jubiel.notifications.dismissed", JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const navItems: { key: UserNavKey; label: string; to: string }[] = [
    {
      key: "events",
      label: t("navigation.events"),
      to: USER_ROUTES.events(currentLocale),
    },
    {
      key: "pachete",
      label: t("navigation.pachete"),
      to: USER_ROUTES.packages(currentLocale),
    },
  ];

  // Load user credits
  useEffect(() => {
    if (!user) {
      setCredits(null);
      return;
    }

    let cancelled = false;
    async function loadCredits() {
      try {
        const creditsData = await getUserCredits();
        if (!cancelled) {
          setCredits(creditsData);
        }
      } catch (error) {
        console.error('Failed to load credits:', error);
        if (!cancelled) {
          setCredits({ standard: 0, premium: 0, credits: { standard: [], premium: [] } });
        }
      }
    }

    loadCredits();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const notifications: NotificationItem[] = [];

  if (!user?.email_verified_at) {
    notifications.push({
      id: "verify-email",
      type: "warning",
      title: t("notifications.verifyEmail.title"),
      description: t("notifications.verifyEmail.description", {
        email: user?.user_email ?? "",
      }),
      actionLabel: t("notifications.verifyEmail.action"),
      onAction: () => navigate(PUBLIC_ROUTES.emailVerification(currentLocale)),
      dismissLabel: t("notifications.dismiss"),
      onDismiss: () => dismissNotification("verify-email"),
    });
  }

  // Note: Plan upgrade notifications are now event-specific, not user-specific

  useEffect(() => {
    let cancelled = false;
    async function loadGiftNotifications() {
      try {
        const [events, stripe] = await Promise.all([
          getEvents(),
          fetchStripeAccountStatus().catch(() => null),
        ]);

        const stripeConnected = Boolean(stripe?.connected);

        const items: NotificationItem[] = [];

        for (const ev of events) {
          if (!ev.uuid) continue;
          const eventId = ev.uuid;
          const eventName = ev.name || ev.url || ev.uuid;

          if (!ev.giftsActive) {
            items.push({
              id: `gifts-disabled:${eventId}`,
              type: "warning",
              title: t("notifications.giftsDisabled.title"),
              description: t("notifications.giftsDisabled.description", { eventName }),
              actionLabel: t("notifications.fix"),
              onAction: () => navigate(`/${currentLocale}/editeaza-eveniment/${eventId}?tab=gifts`),
              dismissLabel: t("notifications.dismiss"),
              onDismiss: () => dismissNotification(`gifts-disabled:${eventId}`),
            });
            continue;
          }

          const [registry, gifts] = await Promise.all([
            getGiftRegistry(eventId).catch(() => null),
            getEventGifts(eventId).catch(() => null),
          ]);

          const anyEnabledFund = Boolean(registry?.funds?.some((f: Record<string, unknown>) => f?.enabled));
          const anyEnabledGift = Boolean(gifts?.gifts?.some((g: Record<string, unknown>) => g?.enabled));

          if (!anyEnabledFund && !anyEnabledGift) {
            items.push({
              id: `no-enabled-gifts:${eventId}`,
              type: "warning",
              title: t("notifications.noEnabledGifts.title"),
              description: t("notifications.noEnabledGifts.description", { eventName }),
              actionLabel: t("notifications.fix"),
              onAction: () => navigate(`/${currentLocale}/editeaza-eveniment/${eventId}?tab=gifts`),
              dismissLabel: t("notifications.dismiss"),
              onDismiss: () => dismissNotification(`no-enabled-gifts:${eventId}`),
            });
          }

          if ((anyEnabledFund || anyEnabledGift) && !stripeConnected) {
            items.push({
              id: `stripe-not-connected:${eventId}`,
              type: "warning",
              title: t("notifications.stripeNotConnected.title"),
              description: t("notifications.stripeNotConnected.description", { eventName }),
              actionLabel: t("notifications.fix"),
              onAction: () => navigate(USER_ROUTES.connectedAccounts(currentLocale)),
              dismissLabel: t("notifications.dismiss"),
              onDismiss: () => dismissNotification(`stripe-not-connected:${eventId}`),
            });
          }
        }

        if (!cancelled) {
          setGiftNotifications(items);
        }
      } catch {
        if (!cancelled) {
          setGiftNotifications([]);
        }
      }
    }

    if (user) {
      loadGiftNotifications();
    }

    return () => {
      cancelled = true;
    };
  }, [user, t, navigate, currentLocale]);

  const allNotifications = useMemo(() => {
    const merged = [...notifications, ...giftNotifications];
    return merged.filter((n) => !dismissedNotificationIds.includes(n.id));
  }, [notifications, giftNotifications, dismissedNotificationIds]);

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(PUBLIC_ROUTES.home(currentLocale))} data-testid="header-logo">
            <h1 className="font-logo text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
              {t("header.logo")}
            </h1>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.key}
                variant={item.key === active ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "text-sm px-4",
                  item.key === active
                    ? "bg-pink-50 text-pink-700 dark:bg-pink-950/30 dark:text-pink-200"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => navigate(item.to)}
              >
                {item.label}
              </Button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {credits && (credits.standard > 0 || credits.premium > 0) && (
            <CreditsDisplay credits={credits} />
          )}
          <LanguageSwitcher compact />
          {user && (
            <UserProfileDropdown
              userName={user.user_name}
              notifications={allNotifications}
            />
          )}
        </div>
      </div>
      {!user?.user_admin && <ActiveEventReturn userUuid={user?.uuid} />}
    </header>
  );
}
